import React, { useEffect, useMemo, useState } from 'react';
import { Divider, Form } from '@duxton-web/ui';

import { getAssignmentStrategy, getVariables } from '../../../constants';
import { useExperimentContext } from '../../../contexts/experiment-context';
import AssignmentStrategyOptions from './assignment-strategy-options';
import CustomTimeSlice from './balance-timeslice';
import ExplicitGroup from './explicit-group';
import PercentageSplit from './percentage-split';
import TimeSlice from './timeslice';
import Service from '@/lib/App';
import {
  ExperimentAssignmentStrategy,
  ExpFormField,
} from '@/pages/ExperimentFormV2/helper/consts';
import AdvancedConfig from '@/pages/ExperimentFormV2/Sections/Treatment/AssignmentStrategy/AdvancedConfig';

const AssignmentStrategy = ({
  personalizeByCountry,
}: {
  personalizeByCountry: boolean;
}) => {
  const experimentContext = useExperimentContext();
  const { experiment, configurations } = experimentContext;
  const experimentDomain = experiment?.domain;
  const variables = getVariables(configurations);
  const currentStrategy = getAssignmentStrategy(configurations);

  const form = Form.useFormInstance();

  const [timeSliceType, setTimeSliceType] = useState<string>('');

  useEffect(() => {
    if (experimentDomain) {
      setTimeSliceType('');
    }
  }, [experimentDomain]);

  useEffect(() => {
    if (
      !timeSliceType &&
      currentStrategy &&
      [
        ExperimentAssignmentStrategy.TIME_SLICE_SHUFFLE,
        ExperimentAssignmentStrategy.TIME_SLICE,
        ExperimentAssignmentStrategy.TIME_SLICE_V2,
        ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE,
      ].includes(currentStrategy)
    ) {
      setTimeSliceType(currentStrategy);
    }
  }, [timeSliceType, currentStrategy]);

  const availableStrategies = useMemo(() => {
    let strategies = Service.cache.getAllStrategies();
    const domainlessFeatureFlag = Service.cache.isFeatureFlagOn('domainless');
    if (experimentDomain && !domainlessFeatureFlag) {
      return strategies.filter(
        (strategy) =>
          strategy?.allowedDomains &&
          strategy?.allowedDomains?.includes(experimentDomain),
      );
    }
    return strategies;
  }, [experimentDomain]);

  const currentDomainMetadata = useMemo(() => {
    const allDomainMetadata = Service?.cache?.getAllDomainMetadata();
    const result = allDomainMetadata?.find(
      (metadata) => metadata.name === experimentDomain,
    );
    return result;
  }, [experimentDomain]);

  useEffect(() => {
    if (
      currentDomainMetadata?.strategies?.includes(
        ExperimentAssignmentStrategy.TIME_SLICE_V2,
      ) &&
      currentStrategy === ExperimentAssignmentStrategy.TIME_SLICE
    ) {
      form.setFieldValue(
        ExpFormField.STRATEGY.NAME,
        ExperimentAssignmentStrategy.TIME_SLICE_V2,
      );
      setTimeSliceType(ExperimentAssignmentStrategy.TIME_SLICE_V2);
    }
  }, [currentDomainMetadata, currentStrategy]);

  return (
    <>
      <Divider
        orientation="left"
        orientationMargin="0"
        style={{
          fontSize: 10,
          textTransform: 'uppercase',
          fontWeight: 700,
          marginInline: 0,
          margin: '8px 0',
        }}
      >
        Assignment Strategy
      </Divider>
      <AssignmentStrategyOptions personalizeByCountry={personalizeByCountry} />
      <Divider style={{ marginInline: 0, margin: '8px 0' }} />
      <TimeSlice />
      <PercentageSplit />
      {currentStrategy ===
        ExperimentAssignmentStrategy.GEO_TIME_SLICE_SHUFFLE &&
        variables.length > 0 && (
          <>
            <Divider />
            <AdvancedConfig
              strategy={availableStrategies.find(
                (item) => item.name === currentStrategy,
              )}
            />
          </>
        )}
      <CustomTimeSlice />
      <ExplicitGroup />
    </>
  );
};

export default AssignmentStrategy;
